import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AiAgentService } from './API/ai-agent.service';
import { AudioCaptureService } from './audio-capture.service';
import { VadEvento } from '../utils/vad-detector';

/** Estado observável da escuta (lido pelo popover de configuração — OnPush-safe). */
export type VoiceStatus =
  | 'desligado'
  | 'sem-suporte'
  | 'permissao-negada'
  | 'erro'
  | 'ouvindo';

/**
 * Quantos eventos o log de diagnóstico guarda em memória.
 * Um array que crescesse sem limite numa conversa longa seria vazamento de memória — por
 * isso cada novo evento entra na frente e o log é truncado aqui.
 */
const MAX_EVENTOS_DIAGNOSTICO = 20;

/**
 * Modo de voz hands-free do assistente One.
 *
 * A captura em si é do {@link AudioCaptureService} — este serviço só orquestra o modo conversa:
 * liga/desliga, reage aos eventos do VAD e conversa com o {@link AiAgentService}.
 *
 * Por que NÃO usamos `webkitSpeechRecognition`: ele envia o áudio aos servidores do Google e a
 * rede corporativa bloqueia esse tráfego (erro `network` confirmado em diagnóstico). O microfone
 * em si funciona — o medidor de volume, 100% local, sempre funcionou.
 */
@Injectable({ providedIn: 'root' })
export class VoiceHotwordService {
  private readonly ai = inject(AiAgentService);
  private readonly captura = inject(AudioCaptureService);

  /**
   * Modo conversa ligado — signal, e não campo simples, porque `status` deriva dele: enquanto
   * `status` derivava só de `captura.status()`, o push-to-talk (que também abre a captura) fazia
   * o popover exibir "ouvindo" com o modo conversa desligado, e o clique seguinte LIGAVA em vez
   * de desligar. A verdade sobre o modo conversa é deste serviço; a captura só diz se o
   * microfone abriu.
   */
  private readonly conversaAtiva = signal(false);
  private enviando = false;

  readonly nivel = this.captura.nivel;
  readonly dispositivos = this.captura.dispositivos;
  readonly dispositivoSelecionado = this.captura.dispositivoSelecionado;
  readonly gravando = signal<boolean>(false);
  readonly erro = computed(() => this.captura.erro());

  /** Piso de ruído adaptativo do VAD (repassado do {@link AudioCaptureService}). */
  readonly noiseFloor = this.captura.noiseFloor;
  /** Limiar que dispara "fala-comecou", lido do próprio VadDetector via captura — fonte ÚNICA
   *  da fórmula. Quando ela vivia duplicada aqui, a UI podia exibir um limiar diferente do que
   *  o detector realmente aplicava. */
  readonly limiarInicio = this.captura.limiarInicio;
  /** Log dos últimos eventos do VAD, mais recente primeiro — alimenta o painel de
   *  "Diagnóstico técnico" do popover. Truncado em {@link MAX_EVENTOS_DIAGNOSTICO}. */
  readonly eventos = signal<string[]>([]);
  /** Quantas falas completas foram detectadas mas NÃO enviadas por já haver envio em curso.
   *  Existe para que essa perda seja visível — antes ela era silenciosa. */
  readonly falasIgnoradas = signal<number>(0);
  readonly status = computed<VoiceStatus>(() => {
    const s = this.captura.status();
    if (s === 'sem-suporte') return 'sem-suporte';
    if (s === 'permissao-negada') return 'permissao-negada';
    if (s === 'erro') return 'erro';
    // "ouvindo" = MODO CONVERSA ligado, não "microfone aberto": o push-to-talk também abre a
    // captura, e o popover não pode anunciar conversa onde não há.
    return this.conversaAtiva() ? 'ouvindo' : 'desligado';
  });

  private readonly listening = new BehaviorSubject<boolean>(false);
  readonly isListening$: Observable<boolean> = this.listening.asObservable();

  constructor() {
    // O VAD precisa saber quando a IA está falando: durante o TTS o limiar sobe (barge-in) e o
    // piso de ruído para de aprender, para não se contaminar com a própria voz da IA.
    effect(() => {
      const estado = this.ai.estado();
      this.captura.definirIaFalando(estado === 'falando');
    });
  }

  suportado(): boolean {
    return this.captura.suportado();
  }

  /** Liga ou desliga o modo conversa. */
  async alternar(): Promise<boolean> {
    if (this.conversaAtiva()) {
      this.parar();
      return false;
    }
    this.falasIgnoradas.set(0);
    // Dono 'conversa': se o push-to-talk já tiver o microfone aberto, compartilha o mesmo grafo.
    const ok = await this.captura.abrir('conversa', e => this.aoEvento(e));
    this.conversaAtiva.set(ok);
    this.listening.next(ok);
    return ok;
  }

  parar(): void {
    this.conversaAtiva.set(false);
    this.gravando.set(false);
    // Devolve APENAS a reserva do modo conversa — se o push-to-talk estiver gravando, o
    // microfone continua com ele.
    this.captura.fechar('conversa');
    this.listening.next(false);
  }

  async listarDispositivos(): Promise<MediaDeviceInfo[]> {
    return this.captura.listarDispositivos();
  }

  definirDispositivo(id: string): void {
    this.captura.definirDispositivo(id);
    if (!this.conversaAtiva()) return;
    if (this.captura.reservadoPor('manual')) {
      // Push-to-talk está com o microfone: `parar()` não derruba o grafo (a reserva 'manual'
      // ainda existe) e o `abrir('conversa')` seguinte curto-circuitaria em `ativo` sem recriar
      // nada — trocar de dispositivo aqui seria um no-op fingindo sucesso. Recriar o grafo à
      // força, por sua vez, invalidaria a régua (AudioTimeline) da gravação manual em curso.
      // Mais simples e seguro: recusar agora e avisar no log de diagnóstico — a troca se aplica
      // sozinha da próxima vez que o modo conversa abrir o microfone.
      this.registrarTexto('troca de microfone adiada — solte o push-to-talk para aplicar');
      return;
    }
    // Reabre com o novo dispositivo.
    this.parar();
    void this.alternar();
  }

  private aoEvento(e: VadEvento): void {
    this.registrarEvento(e);
    switch (e.tipo) {
      case 'barge-in':
        // O usuário falou por cima: cala a IA na hora. A captura já está em curso e o pre-roll
        // garante que a primeira sílaba não se perdeu.
        this.ai.pararFala();
        this.gravando.set(true);
        break;
      case 'fala-comecou':
        this.gravando.set(true);
        break;
      case 'fala-descartada':
        this.gravando.set(false);
        break;
      case 'fala-terminou':
        this.gravando.set(false);
        void this.enviar(e.inicioMs, e.fimMs);
        break;
    }
  }

  /**
   * Acumula o evento no log de diagnóstico (mais recente primeiro), truncado em
   * {@link MAX_EVENTOS_DIAGNOSTICO}. É o dado que, no passado, evidenciou visualmente que a
   * rede corporativa bloqueava a Web Speech API — sem log na tela, aquele diagnóstico não
   * teria sido possível.
   */
  private registrarEvento(e: VadEvento): void {
    this.registrarTexto(this.formatarEvento(e));
  }

  private registrarTexto(texto: string): void {
    const hora = new Date().toLocaleTimeString('pt-BR', { hour12: false });
    this.eventos.update(lista => [`${hora} · ${texto}`, ...lista].slice(0, MAX_EVENTOS_DIAGNOSTICO));
  }

  private formatarEvento(e: VadEvento): string {
    switch (e.tipo) {
      case 'fala-comecou':
        return `fala começou (t=${e.tMs.toFixed(0)}ms)`;
      case 'fala-terminou':
        return `fala terminou (${(e.fimMs - e.inicioMs).toFixed(0)}ms)`;
      case 'fala-descartada':
        return `fala descartada (motivo: ${e.motivo})`;
      case 'barge-in':
        return `barge-in — interrompeu a IA (t=${e.tMs.toFixed(0)}ms)`;
    }
  }

  private async enviar(inicioMs: number, fimMs: number): Promise<void> {
    if (this.enviando) {
      // Cenário real: "quanto vendi ontem" → pausa → "de camiseta". O segundo trecho chega com o
      // primeiro ainda em voo (estado `pensando`) e não pode ser enviado — mas TAMBÉM não pode
      // desaparecer calado: antes ele aparecia no log como fala terminada e nunca era enviado.
      // Por isso o VAD segue livre durante `pensando` (bloqueá-lo apenas trocaria um descarte
      // silencioso por outro) e a perda é anunciada aqui, no log e num contador visível.
      this.registrarTexto(
        `fala IGNORADA (${(fimMs - inicioMs).toFixed(0)}ms) — resposta anterior ainda em curso; repita`,
      );
      this.falasIgnoradas.update(n => n + 1);
      return;
    }
    this.enviando = true;
    try {
      const wav = await this.captura.extrairWav(inicioMs, fimMs);
      // > 0, não um tamanho mínimo de WAV: o VAD já garante falaMinimaMs antes de emitir
      // "fala-terminou" — esta guarda só existe para não mandar um blob vazio ao backend.
      if (wav.size > 0) {
        this.ai.abrir();
        await this.ai.enviarVoz(wav);
      }
    } finally {
      this.enviando = false;
    }
  }
}
